import React, { useContext } from "react";
import classNames from "classnames";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { ArAgingBucket } from "@common/types/reports/ar-aging";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useViewTableMeta } from "@hooks/view/useViewTableMeta";
import { AccountingReportContext } from "../AccountingReport.context";
import { useReportAmountDisplay } from "../useReportAmountDisplay";
import {
    ArAgingSummaryTableCallbacks,
    getArAgingSummaryDrillDownParams,
} from "./arAgingDetailDrillDown.lib";
import { ArAgingSummaryViewRow } from "./arAgingSummaryViewTypes";
import styles from "./ArAgingSummaryTable.module.scss";

interface MakeBucketCellOptions {
    bucket: ArAgingBucket;
}

/**
 * A bucket amount cell drills into AR Aging Detail scoped to this customer +
 * bucket. The synthetic Total row's bucket cells drill into Detail scoped to
 * just that bucket (every customer), since there's no single customer to
 * scope to.
 */
export const makeArAgingSummaryBucketCell = ({
    bucket,
}: MakeBucketCellOptions) => {
    const ArAgingSummaryBucketCell = ({
        row,
        table,
    }: CellContext<
        ViewTableFeatures,
        ArAgingSummaryViewRow,
        unknown
    >): React.ReactElement => {
        const { amountsByBucket, customerId, isSummary } = row.original;
        const { filters } = useContext(AccountingReportContext);
        const onDrillDown = useViewTableMeta<
            ArAgingSummaryTableCallbacks,
            unknown
        >(table).callbacks?.onDrillDown;
        const value = amountsByBucket[bucket] ?? 0;
        const { text: displayValue, negativeClassName } =
            useReportAmountDisplay(value);

        const drillDownParams = getArAgingSummaryDrillDownParams({
            filters,
            customerId,
            isSummary,
            bucket,
        });

        return (
            <ViewTableCell
                className={classNames(styles.numericCell, negativeClassName, {
                    [styles.summaryCell]: isSummary,
                })}
            >
                {onDrillDown ? (
                    <button
                        type="button"
                        className={classNames(
                            "data-table__drill-down",
                            styles.drillDownButton,
                        )}
                        onClick={() => onDrillDown(drillDownParams)}
                    >
                        {displayValue}
                    </button>
                ) : (
                    displayValue
                )}
            </ViewTableCell>
        );
    };

    return React.memo(ArAgingSummaryBucketCell);
};
